// src/pages/VehicleUpdate.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Truck, X } from "lucide-react";
import { fetchVehicles } from "../actions/VehicleActions";

const VehicleUpdate = ({ isOpen, onClose, vehicleId, onSuccess }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const authKey =
    auth?.authKey ||
    auth?.userDetails?.authKey ||
    "HQZQpMO3WSjnurGdn4/GZLxCeuEnPB0qwoiVggtWuZc";

  const [vehicleData, setVehicleData] = useState({
    vehicleId: "",
    vehicleType: "",
    weightCat: "",
    status: "A",
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState("");

  const typeMap = {
    C: "Car",
    F: "Forklift",
    V: "Van",
    L: "Lorry",
    S: "SUV",
    B: "Bus",
    CA: "Cab",
    CH: "Cherry Picker",
    T: "Trailer",
    CR: "Crane",
  };

  const getCodeFromName = (name) =>
    Object.keys(typeMap).find((key) => typeMap[key] === name) || "";

  // Fetch vehicle data
  useEffect(() => {
    if (!isOpen || !vehicleId) return;

    const fetchVehicle = async () => {
      setFetching(true);
      setMessage("");
      try {
        const vehicles = await dispatch(fetchVehicles());
        const vehicle = vehicles.find(
          (v) => v.vehicleId?.toUpperCase() === vehicleId?.toUpperCase()
        );

        if (vehicle) {
          setVehicleData({
            vehicleId: vehicle.vehicleId?.substring(0, 8) || "",
            vehicleType:
              getCodeFromName(vehicle.vehicleType) || vehicle.vehicleType?.substring(0, 1),
            weightCat: vehicle.weightCat?.substring(0, 3) || "",
            status: vehicle.status === "Active" ? "A" : "I",
          });
        } else {
          setMessage("⚠️ Vehicle not found.");
        }
      } catch (err) {
        console.error("Error fetching vehicle:", err);
        setMessage("❌ Failed to fetch vehicle details.");
      } finally {
        setFetching(false);
      }
    };

    fetchVehicle();
  }, [vehicleId, authKey, dispatch, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "weightCat") {
        // Allow only alphanumeric for Weight Category
        formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    }

    setVehicleData((prev) => ({ ...prev, [name]: formattedValue }));
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      // Fetch latest vehicle to get driver & oStatus
      const vehicles = await dispatch(fetchVehicles());
      const vehicle = vehicles.find(
        (v) => v.vehicleId?.toUpperCase() === vehicleData.vehicleId?.toUpperCase()
      );

      if (!vehicle) {
        setMessage("❌ Vehicle not found for update.");
        setLoading(false);
        return;
      }

      // ❌ BLOCK if vehicle is ONLINE
      if (vehicle.oStatus === "Online") {
        setMessage("❌ Cannot update vehicle while it is ONLINE.");
        setLoading(false);
        return;
      }

      // ❌ NEW RULE — BLOCK Active → Inactive IF driver assigned
      if (vehicle.driverSno && vehicleData.status === "I") {
        setMessage(
          "❌ Cannot mark this vehicle as INACTIVE while a driver is assigned."
        );
        setLoading(false);
        return;
      }

      // Keep existing driver to avoid FK conflict
      const payload = [
        {
          vehicleId: vehicleData.vehicleId.substring(0, 8),
          vehicleType: vehicleData.vehicleType,
          weightCat: vehicleData.weightCat.substring(0, 3),
          driverSno: vehicle.driverSno, // keep existing driver
          status: vehicleData.status,
          bStatus: "A",
          latitude: "0",
          longitude: "0",
        },
      ];

      const response = await axios.post(
        "https://aeroviewbackend.dockyardsoftware.com/VehicleRegister/PostVehicleDetails",
        payload,
        { headers: { "Content-Type": "application/json", "auth-key": authKey } }
      );

      if (response.data.statusCode === 200) {
        setMessage("✅ Vehicle updated successfully!");
        if (onSuccess) onSuccess();
        setTimeout(() => {
            setMessage("");
            onClose();
        }, 1200);
      } else {
        setMessage("❌ Update failed: " + response.data.result);
      }
    } catch (err) {
      console.error("Axios Error:", err);
      setMessage("❌ Failed to update vehicle. Check network/API.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 transition-opacity duration-300">
      <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 max-w-sm w-full border border-white/50 relative animate-in fade-in zoom-in duration-200">
        
        {/* Close Button */}
        <button 
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition-colors"
        >
            <X size={20} />
        </button>

        <div className="flex items-center justify-center mb-4 space-x-2">
          <div className="bg-blue-100 p-2 rounded-full">
            <Truck size={24} className="text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Update Vehicle</h2>
        </div>

        {message && (
            <div className={`text-xs p-2 rounded-lg mb-3 text-center border ${
                message.startsWith("✅") 
                ? "bg-green-50 text-green-600 border-green-100" 
                : "bg-red-50 text-red-600 border-red-100"
            }`}>
              {message}
            </div>
        )}

        {fetching ? (
            <div className="text-center py-8 text-gray-500">Loading details...</div>
        ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle ID</label>
                    <input
                    type="text"
                    name="vehicleId"
                    value={vehicleData.vehicleId}
                    disabled
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Type</label>
                    <select
                    name="vehicleType"
                    value={vehicleData.vehicleType}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    >
                    <option value="">Select Vehicle Type</option>
                    {Object.entries(typeMap).map(([code, name]) => (
                        <option key={code} value={code}>
                        {name}
                        </option>
                    ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Weight Category</label>
                    <input
                    type="text"
                    name="weightCat"
                    value={vehicleData.weightCat}
                    onChange={handleChange}
                    placeholder="Max 3 chars"
                    maxLength={3}
                    required
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select
                    name="status"
                    value={vehicleData.status}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    >
                    <option value="A">Active</option>
                    <option value="I">Inactive</option>
                    </select>
                </div>

                <div className="flex gap-3 mt-4 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg shadow-md hover:bg-blue-700 text-sm font-medium transition-all transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? "Updating..." : "Update"}
                    </button>
                </div>
            </form>
        )}
      </div>
    </div>
  );
};

export default VehicleUpdate;
